<template>
  <b-modal v-model="localVisible" title="edit column" @ok="onSave" @hide="onHide" size="lg" :ok-disabled="uniqueTitleBlocked">
    <template #title>
      edit column
      <modal-help topic="edit-column" />
    </template>
    <b-container fluid>
      <b-alert v-if="serverError" variant="danger" show>{{ serverError }}</b-alert>
      <b-form>
        <b-row>
          <b-form-group label="column title">
            <b-form-input v-model="formTitle" placeholder="my updated spreadsheet column name" required />
          </b-form-group>
        </b-row>
        <b-row>
          <b-form-group label="unique title" :state="uniqueTitleState" :invalid-feedback="uniqueTitleFeedback">
            <b-form-input v-model="formUniqueTitle" :state="uniqueTitleState" placeholder="my_updated_spreadsheet_column_name" required />
            <small v-if="formType === 'relationship' && !uniqueTitleFeedback" class="text-muted">Relationship names have to be unique in the application.</small>
          </b-form-group>
        </b-row>
        <b-row>
          <b-form-group label="column type">
            <b-form-select v-model="formType" :options="columnTypes" class="mb-3" size="sm" placeholder="select updated column type" required />
          </b-form-group>
        </b-row>
        <b-row v-if="formType === 'relationship'">
          <b-form-group label="related spreadsheet">
            <b-form-select v-model="formRelatedSpreadSheet" :options="spreadSheetOptions" class="mb-3" size="sm" placeholder="select related spreadsheet" required />
          </b-form-group>
        </b-row>
        <b-row v-if="formType === 'relationship'">
          <b-form-group label="relationship type">
            <b-form-select v-model="formRelationshipType" :options="computedRelationshipTypes" class="mb-3" size="sm" placeholder="select relationship type" required />
          </b-form-group>
        </b-row>
        <relationship-column-settings
          v-if="formType === 'relationship'"
          v-model="formRelationshipSettings"
          :related-columns="relatedSheetColumns"
          :owner-columns="currentSpreadsheetColumns"
          :related-spread-sheet="formRelatedSpreadSheet"
        />
        <b-row v-if="formType === 'dateTime'">
          <b-form-group label="date only">
            <b-form-checkbox v-model="formDateOnly">Store a calendar date, with no time</b-form-checkbox>
            <small class="text-muted">The day stays the same for every viewer. A date and time is stored in UTC and shown in the viewer's time zone.</small>
          </b-form-group>
        </b-row>
        <b-row v-if="formType === 'image'">
          <b-form-group label="auto-resize">
            <b-form-checkbox v-model="formAutoResize">Resize on upload</b-form-checkbox>
            <div v-if="formAutoResize" class="mt-2">
              <b-row>
                <b-col cols="6">
                  <label class="form-label" style="font-size: 0.85rem;">{{ formKeepProportions ? 'max width (px)' : 'width (px)' }}</label>
                  <b-form-input v-model.number="formResizeWidth" type="number" min="1" size="sm" />
                </b-col>
                <b-col cols="6">
                  <label class="form-label" style="font-size: 0.85rem;">{{ formKeepProportions ? 'max height (px)' : 'height (px)' }}</label>
                  <b-form-input v-model.number="formResizeHeight" type="number" min="1" size="sm" />
                </b-col>
              </b-row>
              <b-form-checkbox v-model="formKeepProportions" class="mt-2">Keep proportions</b-form-checkbox>
              <small class="text-muted d-block mt-1">{{ imageResizeHelp }}</small>
            </div>
          </b-form-group>
        </b-row>
        <b-row v-if="formType === 'multiselect'">
          <b-form-group label="selection mode">
            <b-form-select v-model="formSelectionMode" :options="selectionModeOptions" class="mb-3" size="sm" />
            <small class="text-muted">Single select saves one value. Multiple select saves every chosen value. The form chooses whether to show checkboxes, radio buttons, a dropdown, or a multiple select list.</small>
          </b-form-group>
        </b-row>
        <b-row v-if="formType === 'multiselect'">
          <b-form-group label="choices">
            <small class="text-muted">Selectable values for this field.</small>
            <div v-for="(choice, index) in formChoices" :key="index" class="choice-row">
              <b-form-input v-model="formChoices[index]" size="sm" placeholder="choice" />
              <span class="choice-remove" @click="removeChoice(index)">x</span>
            </div>
            <div class="add-choice" @click="addChoice">+ add choice</div>
          </b-form-group>
        </b-row>
        <b-row v-if="formType === 'duration'">
          <b-form-group label="unit">
            <b-form-select v-model="formDurationUnit" :options="durationUnitOptions" class="mb-3" size="sm" />
            <small class="text-muted">The form asks for a start and an end. The column stores the time between them in this unit. A formula adds that number with ADD_DURATION and the same unit.</small>
          </b-form-group>
        </b-row>
        <b-row v-if="formType === 'sequence'">
          <b-form-group label="start at">
            <b-form-input v-model.number="formSequenceStart" type="number" step="1" size="sm" />
            <small class="text-muted">The first record gets this number. Later records continue after the highest number already stored. This column is not on a form and cannot be edited.</small>
          </b-form-group>
        </b-row>
        <b-row v-if="formType === 'signature'">
          <small class="text-muted d-block mb-3">A person draws on a canvas. The drawing is stored as an image, along with who signed and when.</small>
        </b-row>
        <b-row v-if="formType === 'formula'">
          <b-form-group label="formula">
            <formula-editor
              v-model="formFormula"
              :columns="currentSpreadsheetColumns"
              :spreadsheets="allSpreadsheets"
              :current-spreadsheet-unique-title="currentSpreadsheetUniqueTitle"
            />
          </b-form-group>
        </b-row>

        <!-- Unique Constraint -->
        <b-row v-if="supportsUniqueConstraint" class="mt-3">
          <b-col>
            <b-form-checkbox v-model="formUnique">Enforce unique values</b-form-checkbox>
          </b-col>
        </b-row>

        <b-row class="mt-3">
          <b-col>
            <b-form-group label="description" description="Shown under the field. A form can replace the label, the placeholder, and this description on that screen.">
              <b-form-textarea v-model="formDescription" rows="2" max-rows="4" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row v-if="supportsColumnRequired">
          <b-col>
            <b-form-checkbox v-model="formRequired">Required</b-form-checkbox>
            <small class="text-muted d-block">A save without this value is rejected on a form, an inline cell, this grid, the API, and a workflow. Import can leave it empty.</small>
          </b-col>
        </b-row>
        <b-row v-if="supportsColumnDefault" class="mt-2">
          <b-col>
            <b-form-group label="default value" description="Fills an empty value when a record is created. A form rule can still set a value from other answers.">
              <b-form-input v-model="formDefaultValue" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row class="mt-3">
          <b-col>
            <b-button size="sm" variant="outline-secondary" @click="validationEditorVisible = true">
              Validation rules
              <span v-if="formValidationRules.length"> ({{ formValidationRules.length }})</span>
            </b-button>
            <small class="text-muted d-block mt-1">The first matching rule blocks or allows the save. The same check runs on every save.</small>
          </b-col>
        </b-row>
        <validation-rule-editor
          :visible="validationEditorVisible"
          :rules="formValidationRules"
          :column="validationColumn"
          :columns="currentSpreadsheetColumns"
          @update:visible="validationEditorVisible = $event"
          @save="formValidationRules = $event"
        />
        <b-row v-if="supportsValueRules" class="mt-3">
          <b-col>
            <b-button size="sm" variant="outline-secondary" @click="valueEditorVisible = true">
              Value rules
              <span v-if="formValueRules.length"> ({{ formValueRules.length }})</span>
            </b-button>
            <small class="text-muted d-block mt-1">The first matching rule sets this column on every save. The value can be typed, copied from another column, or copied from a related record.</small>
          </b-col>
        </b-row>
        <value-rule-editor
          :visible="valueEditorVisible"
          :rules="formValueRules"
          :column="validationColumn"
          :columns="currentSpreadsheetColumns"
          :spreadsheets="allSpreadsheets"
          :spreadsheet-unique-title="currentSpreadsheetUniqueTitle"
          @update:visible="valueEditorVisible = $event"
          @save="formValueRules = $event"
        />

        <!-- Value Formatting Section -->
        <b-row v-if="supportsValueFormatting" class="mt-3">
          <b-col>
            <div class="value-formatting-section">
              <div class="section-header" @click="showValueFormatting = !showValueFormatting" style="cursor: pointer; user-select: none;">
                <strong>Value Formatting</strong>
                <span v-if="hasValueFormatting" class="badge bg-primary ms-2" style="font-size: 0.7rem;">active</span>
                <span class="float-end">{{ showValueFormatting ? '▾' : '▸' }}</span>
              </div>

              <div v-if="showValueFormatting" class="mt-2">

                <!-- Number formatting -->
                <template v-if="effectiveFormatType === 'number'">
                  <b-row class="mb-2">
                    <b-col cols="6">
                      <label class="form-label" style="font-size: 0.85rem;">Decimal Places</label>
                      <b-form-input v-model.number="vfDecimalPlaces" type="number" min="0" max="10" size="sm" placeholder="auto" />
                    </b-col>
                    <b-col cols="6">
                      <label class="form-label" style="font-size: 0.85rem;">Currency</label>
                      <b-form-select v-model="vfCurrency" :options="currencyOptions" size="sm" />
                    </b-col>
                  </b-row>
                  <b-row class="mb-2">
                    <b-col cols="6">
                      <label class="form-label" style="font-size: 0.85rem;">Thousands Separator</label>
                      <b-form-select v-model="vfThousandsSeparator" :options="thousandsSeparatorOptions" size="sm" />
                    </b-col>
                    <b-col cols="6">
                      <label class="form-label" style="font-size: 0.85rem;">Decimal Separator</label>
                      <b-form-select v-model="vfDecimalSeparator" :options="decimalSeparatorOptions" size="sm" />
                    </b-col>
                  </b-row>
                  <b-row class="mb-2">
                    <b-col cols="6">
                      <label class="form-label" style="font-size: 0.85rem;">Rounding</label>
                      <b-form-select v-model="vfRounding" :options="roundingOptions" size="sm" />
                    </b-col>
                    <b-col cols="6">
                      <b-form-checkbox v-model="vfPercentMode" class="mt-4">Display as Percentage</b-form-checkbox>
                    </b-col>
                  </b-row>
                  <b-row class="mb-2" v-if="numberPreview !== null">
                    <b-col>
                      <div class="text-muted" style="font-size: 0.8rem;">
                        Preview: <strong>{{ numberPreview }}</strong>
                      </div>
                    </b-col>
                  </b-row>
                </template>

                <!-- Text formatting -->
                <template v-if="effectiveFormatType === 'text'">
                  <b-row class="mb-2">
                    <b-col cols="6">
                      <label class="form-label" style="font-size: 0.85rem;">Max Display Length</label>
                      <b-form-input v-model.number="vfMaxLength" type="number" min="1" size="sm" placeholder="unlimited" />
                    </b-col>
                    <b-col cols="6">
                      <label class="form-label" style="font-size: 0.85rem;">Case Transform</label>
                      <b-form-select v-model="vfCaseTransform" :options="caseTransformOptions" size="sm" />
                    </b-col>
                  </b-row>
                  <b-row class="mb-2">
                    <b-col cols="4">
                      <b-form-checkbox v-model="vfStripHtml">Strip HTML</b-form-checkbox>
                    </b-col>
                    <b-col cols="4">
                      <b-form-checkbox v-model="vfStripNonAlphanumeric">Strip Non-Alphanumeric</b-form-checkbox>
                    </b-col>
                    <b-col cols="4">
                      <b-form-checkbox v-model="vfTrimWhitespace">Trim Whitespace</b-form-checkbox>
                    </b-col>
                  </b-row>
                  <b-row class="mb-2">
                    <b-col cols="6">
                      <label class="form-label" style="font-size: 0.85rem;">Font</label>
                      <b-form-select v-model="vfFontFamily" :options="fontFamilyOptions" size="sm" />
                    </b-col>
                    <b-col cols="6">
                      <label class="form-label" style="font-size: 0.85rem;">Font size</label>
                      <b-form-input v-model.number="vfFontSize" type="number" min="8" max="96" size="sm" placeholder="default" />
                    </b-col>
                  </b-row>
                  <b-row class="mb-2">
                    <b-col cols="4">
                      <b-form-checkbox v-model="vfBold">Bold</b-form-checkbox>
                    </b-col>
                    <b-col cols="4">
                      <b-form-checkbox v-model="vfItalic">Italic</b-form-checkbox>
                    </b-col>
                    <b-col cols="4">
                      <b-form-checkbox v-model="vfStrike">Strikethrough</b-form-checkbox>
                    </b-col>
                  </b-row>
                  <div class="mb-2">
                    <label class="form-label" style="font-size: 0.85rem;">Text replacements</label>
                    <div v-for="(row, index) in vfReplacements" :key="index" class="d-flex gap-2 mb-1">
                      <b-form-input v-model="row.find" size="sm" placeholder="find" />
                      <b-form-input v-model="row.replace" size="sm" placeholder="replace with" />
                      <b-button size="sm" variant="outline-secondary" @click="vfReplacements.splice(index, 1)">x</b-button>
                    </div>
                    <b-button size="sm" variant="outline-secondary" @click="vfReplacements.push({ find: '', replace: '' })">add replacement</b-button>
                  </div>
                </template>

                <!-- Clear button -->
                <b-row v-if="hasValueFormatting" class="mt-2">
                  <b-col>
                    <b-button size="sm" variant="outline-secondary" @click="clearValueFormatting">Clear Formatting</b-button>
                  </b-col>
                </b-row>
              </div>
            </div>
          </b-col>
        </b-row>

        <b-row>
          <b-form-group label="delete column">
            <b-button @click="$emit('show-delete')">delete column</b-button>
          </b-form-group>
        </b-row>
      </b-form>
    </b-container>
  </b-modal>
</template>

<script>
import FormulaEditor from './FormulaEditor.vue'
import RelationshipColumnSettings from './lib/RelationshipColumnSettings.vue'
import ValidationRuleEditor from './lib/ValidationRuleEditor.vue'
import ValueRuleEditor from './lib/ValueRuleEditor.vue'
import { isTemplateFormula } from '../lib/formula-utils.js'
import { columnSupportsDefault, columnSupportsRequired } from '../../../shared/column-write.js'
import { relationshipUniqueTitleConflict, relationshipUniqueTitleConflictMessage } from '../../../shared/relationship-column.js'
import { columnAcceptsValueRule } from '../../../shared/column-value-rules.js'
import { formatFieldDisplay } from '../../../shared/value-format.js'

const CURRENCIES = {
  USD: { symbol: '$' },
  EUR: { symbol: '€' },
  GBP: { symbol: '£' },
  JPY: { symbol: '¥' },
  CAD: { symbol: 'CA$' },
  AUD: { symbol: 'A$' },
  CHF: { symbol: 'CHF' },
  CNY: { symbol: '¥' },
  INR: { symbol: '₹' },
  BRL: { symbol: 'R$' }
}

export default {
  components: {
    FormulaEditor,
    RelationshipColumnSettings,
    ValidationRuleEditor,
    ValueRuleEditor
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    column: {
      type: Object,
      default: () => ({})
    },
    columnTypes: {
      type: Array,
      default: () => []
    },
    spreadSheetOptions: {
      type: Array,
      default: () => []
    },
    currentSpreadsheetColumns: {
      type: Array,
      default: () => []
    },
    allSpreadsheets: {
      type: Array,
      default: () => []
    },
    currentSpreadsheetUniqueTitle: {
      type: String,
      default: ''
    },
    serverError: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      localVisible: false,
      submitAttempted: false,
      formTitle: '',
      formUniqueTitle: '',
      formType: '',
      formRelationshipType: '',
      formRelatedSpreadSheet: '',
      formFormula: '',
      formUnique: false,
      formRequired: false,
      formDescription: '',
      formDefaultValue: '',
      formValidationRules: [],
      validationEditorVisible: false,
      formValueRules: [],
      valueEditorVisible: false,
      formSelectionMode: 'multiple',
      formChoices: [],
      formAutoResize: false,
      formResizeWidth: null,
      formResizeHeight: null,
      formKeepProportions: false,
      formDateOnly: false,
      formDurationUnit: 'hours',
      formSequenceStart: 1,
      formRelationshipSettings: {
        displayFormula: '',
        cascadeFilters: [],
        cascadeMatchMode: 'all',
        showAllUntilParent: false,
        allowAdd: false,
        addFields: []
      },
      durationUnitOptions: [
        { value: 'seconds', text: 'seconds' },
        { value: 'minutes', text: 'minutes' },
        { value: 'hours', text: 'hours' },
        { value: 'days', text: 'days' }
      ],
      selectionModeOptions: [
        { value: 'single', text: 'single select' },
        { value: 'multiple', text: 'multiple select' }
      ],
      showValueFormatting: false,
      // Number formatting
      vfDecimalPlaces: null,
      vfThousandsSeparator: null,
      vfDecimalSeparator: '.',
      vfCurrency: null,
      vfPercentMode: false,
      vfRounding: null,
      vfMaxLength: null,
      vfStripHtml: false,
      vfStripNonAlphanumeric: false,
      vfTrimWhitespace: false,
      vfCaseTransform: null,
      vfReplacements: [],
      vfBold: false,
      vfItalic: false,
      vfStrike: false,
      vfFontFamily: null,
      vfFontSize: null,
      // Options
      currencyOptions: [
        { value: null, text: 'None' },
        ...Object.keys(CURRENCIES).map(k => ({ value: k, text: `${k} (${CURRENCIES[k].symbol})` }))
      ],
      thousandsSeparatorOptions: [
        { value: null, text: 'None' },
        { value: ',', text: 'Comma (1,000)' },
        { value: '.', text: 'Period (1.000)' },
        { value: ' ', text: 'Space (1 000)' }
      ],
      decimalSeparatorOptions: [
        { value: '.', text: 'Period (0.50)' },
        { value: ',', text: 'Comma (0,50)' }
      ],
      caseTransformOptions: [
        { value: null, text: 'None' },
        { value: 'uppercase', text: 'UPPERCASE' },
        { value: 'lowercase', text: 'lowercase' },
        { value: 'capitalize', text: 'Capitalize Words' }
      ],
      roundingOptions: [
        { value: null, text: 'Round' },
        { value: 'floor', text: 'Round down' },
        { value: 'ceil', text: 'Round up' }
      ],
      fontFamilyOptions: [
        { value: null, text: 'Default' },
        { value: 'Arial, sans-serif', text: 'Arial' },
        { value: 'Georgia, serif', text: 'Georgia' },
        { value: '"Times New Roman", serif', text: 'Times New Roman' },
        { value: '"Courier New", monospace', text: 'Courier New' },
        { value: 'Verdana, sans-serif', text: 'Verdana' }
      ]
    }
  },
  computed: {
    relatedSheetColumns () {
      const sheet = (this.allSpreadsheets || []).find((item) => item.uniqueTitle === this.formRelatedSpreadSheet)
      return (sheet && sheet.columns) || []
    },
    computedRelationshipTypes () {
      const currentSpreadsheet = (this.allSpreadsheets || []).find(
        s => s.uniqueTitle === this.currentSpreadsheetUniqueTitle
      )
      const currentName = (currentSpreadsheet && currentSpreadsheet.title) || 'current spreadsheet'
      const relatedOption = this.formRelatedSpreadSheet
        ? (this.spreadSheetOptions || []).find(o => o.value === this.formRelatedSpreadSheet)
        : null
      const relatedName = (relatedOption && relatedOption.text) || 'related spreadsheet'
      return [
        { value: 'oneToOne', text: `one to one - one ${currentName} to one ${relatedName}` },
        { value: 'oneToMany', text: `one to many - one ${currentName} to many ${relatedName}` },
        { value: 'manyToOne', text: `many to one - many ${currentName} to one ${relatedName}` },
        { value: 'manyToMany', text: `many to many - many ${currentName} to many ${relatedName}` }
      ]
    },
    imageResizeHelp () {
      if (this.formKeepProportions) {
        return 'The image is scaled so both sides stay within this width and height. Its shape stays the same.'
      }
      return 'The image is stretched to this exact width and height.'
    },
    supportsUniqueConstraint () {
      const excluded = ['image', 'file', 'password', 'formula', 'signature', 'sequence']
      return this.formType && !excluded.includes(this.formType)
    },
    supportsColumnRequired () {
      return columnSupportsRequired({ type: this.formType })
    },
    supportsColumnDefault () {
      return columnSupportsDefault({ type: this.formType })
    },
    validationColumn () {

      return {
        uniqueTitle: this.formUniqueTitle,
        title: this.formTitle,
        type: this.formType
      }
    },
    supportsValueRules () {

      return columnAcceptsValueRule({ type: this.formType })
    },
    effectiveFormatType () {
      if (this.formType === 'number') return 'number'
      if (this.formType === 'text') return 'text'
      if (this.formType === 'formula') {
        return isTemplateFormula(this.formFormula) ? 'text' : 'number'
      }
      return null
    },
    supportsValueFormatting () {
      return this.effectiveFormatType === 'number' || this.effectiveFormatType === 'text'
    },
    hasValueFormatting () {
      if (this.effectiveFormatType === 'number') {
        return this.vfDecimalPlaces !== null || this.vfThousandsSeparator !== null ||
               this.vfDecimalSeparator !== '.' || this.vfCurrency !== null || this.vfPercentMode ||
               this.vfRounding === 'floor' || this.vfRounding === 'ceil'
      }
      if (this.effectiveFormatType === 'text') {
        return this.vfMaxLength !== null || this.vfStripHtml || this.vfStripNonAlphanumeric ||
               this.vfTrimWhitespace || this.vfCaseTransform !== null || this.vfBold || this.vfItalic ||
               this.vfStrike || this.vfFontFamily || this.vfFontSize ||
               (this.vfReplacements || []).some(row => row && row.find)
      }
      return false
    },
    sameSheetUniqueTitleConflict () {
      const name = String(this.formUniqueTitle || '').trim()
      const current = this.column && this.column.uniqueTitle
      if (!name || name === current) return false
      return (this.currentSpreadsheetColumns || []).some((column) => column && column.uniqueTitle === name)
    },
    relationshipTitleConflict () {
      if (this.formType !== 'relationship' || this.sameSheetUniqueTitleConflict) return null
      return relationshipUniqueTitleConflict(this.allSpreadsheets, this.formUniqueTitle, {
        spreadSheetUniqueTitle: this.currentSpreadsheetUniqueTitle,
        columnUniqueTitle: this.column && this.column.uniqueTitle
      })
    },
    uniqueTitleBlocked () {
      return this.sameSheetUniqueTitleConflict || !!this.relationshipTitleConflict
    },
    uniqueTitleFeedback () {
      if (this.relationshipTitleConflict) return relationshipUniqueTitleConflictMessage(this.relationshipTitleConflict)
      if (this.sameSheetUniqueTitleConflict) return 'this unique title is already in use on this spreadsheet'
      if (this.submitAttempted && !String(this.formUniqueTitle || '').trim()) return 'a unique title is required'
      return ''
    },
    uniqueTitleState () {
      return this.uniqueTitleFeedback ? false : null
    },
    numberPreview () {
      if (this.effectiveFormatType !== 'number' || !this.hasValueFormatting) return null
      const display = formatFieldDisplay(this.vfPercentMode ? 0.4567 : 1234567.89, this.buildValueFormattingOutput())
      return display ? display.text : null
    }
  },
  watch: {
    visible (val) {
      this.localVisible = val
    },
    formUniqueTitle () {
      this.$emit('dismiss-error')
    },
    formType () {
      this.$emit('dismiss-error')
    },
    column: {
      immediate: true,
      handler (col) {
        if (!col) return
        this.submitAttempted = false
        this.formTitle = col.title || ''
        this.formUniqueTitle = col.uniqueTitle || ''
        this.formType = col.type || ''
        this.formRelationshipType = col.relationshipType || ''
        this.formRelatedSpreadSheet = col.relatedSpreadSheet || ''
        this.formFormula = col.formula || ''
        this.formUnique = !!col.unique
        this.formRequired = !!col.required
        this.formDescription = col.description || ''
        this.formDefaultValue = col.defaultValue == null ? '' : String(col.defaultValue)
        this.formValidationRules = (col.validationRules && col.validationRules.rules) ? col.validationRules.rules.slice() : []
        this.formValueRules = (col.valueRules && col.valueRules.rules) ? col.valueRules.rules.slice() : []
        this.formSelectionMode = col.selectionMode === 'single' ? 'single' : 'multiple'
        this.formChoices = Array.isArray(col.choices) ? col.choices.slice() : []
        this.formAutoResize = !!col.autoResize
        this.formResizeWidth = col.resizeWidth || null
        this.formResizeHeight = col.resizeHeight || null
        this.formKeepProportions = !!col.keepProportions
        this.formDateOnly = !!col.dateOnly
        this.formDurationUnit = col.durationUnit || 'hours'
        this.formSequenceStart = col.sequenceStart == null ? 1 : col.sequenceStart
        this.formRelationshipSettings = {
          displayFormula: col.displayFormula || '',
          cascadeFilters: Array.isArray(col.cascadeFilters) ? col.cascadeFilters : [],
          cascadeMatchMode: col.cascadeMatchMode || 'all',
          showAllUntilParent: !!col.showAllUntilParent,
          allowAdd: !!col.allowAdd,
          addFields: Array.isArray(col.addFields) ? col.addFields.slice() : []
        }
        this.initValueFormatting(col.valueFormatting)
      }
    }
  },
  methods: {
    initValueFormatting (vf) {
      if (!vf || !vf.type) {
        this.vfDecimalPlaces = null
        this.vfThousandsSeparator = null
        this.vfDecimalSeparator = '.'
        this.vfCurrency = null
        this.vfPercentMode = false
        this.vfRounding = null
        this.vfMaxLength = null
        this.vfStripHtml = false
        this.vfStripNonAlphanumeric = false
        this.vfTrimWhitespace = false
        this.vfCaseTransform = null
        this.vfReplacements = []
        this.vfBold = false
        this.vfItalic = false
        this.vfStrike = false
        this.vfFontFamily = null
        this.vfFontSize = null
        this.showValueFormatting = false
        return
      }

      this.showValueFormatting = true

      if (vf.type === 'number') {
        this.vfDecimalPlaces = vf.decimalPlaces !== undefined ? vf.decimalPlaces : null
        this.vfThousandsSeparator = vf.thousandsSeparator || null
        this.vfDecimalSeparator = vf.decimalSeparator || '.'
        this.vfCurrency = vf.currency || null
        this.vfPercentMode = vf.percentMode || false
        this.vfRounding = vf.rounding === 'floor' || vf.rounding === 'ceil' ? vf.rounding : null
      } else if (vf.type === 'text') {
        this.vfMaxLength = vf.maxLength !== undefined ? vf.maxLength : null
        this.vfStripHtml = vf.stripHtml || false
        this.vfStripNonAlphanumeric = vf.stripNonAlphanumeric || false
        this.vfTrimWhitespace = vf.trimWhitespace || false
        this.vfCaseTransform = vf.caseTransform || null
        this.vfReplacements = Array.isArray(vf.replacements) ? vf.replacements.map(row => ({ find: row.find || '', replace: row.replace || '' })) : []
        this.vfBold = !!vf.bold
        this.vfItalic = !!vf.italic
        this.vfStrike = !!vf.strike
        this.vfFontFamily = vf.fontFamily || null
        this.vfFontSize = vf.fontSize || null
      }
    },
    clearValueFormatting () {
      this.initValueFormatting(null)
    },
    buildValueFormattingOutput () {
      if (this.effectiveFormatType === 'number') {
        const isDefault = this.vfDecimalPlaces === null && this.vfThousandsSeparator === null &&
                          this.vfDecimalSeparator === '.' && this.vfCurrency === null && !this.vfPercentMode &&
                          this.vfRounding !== 'floor' && this.vfRounding !== 'ceil'
        if (isDefault) return null
        return {
          type: 'number',
          decimalPlaces: this.vfDecimalPlaces,
          rounding: this.vfRounding === 'floor' || this.vfRounding === 'ceil' ? this.vfRounding : null,
          thousandsSeparator: this.vfThousandsSeparator,
          decimalSeparator: this.vfDecimalSeparator || '.',
          currency: this.vfCurrency,
          percentMode: this.vfPercentMode
        }
      }
      if (this.effectiveFormatType === 'text') {
        const replacements = (this.vfReplacements || []).filter(row => row && row.find)
        const isDefault = !this.vfMaxLength && !this.vfStripHtml && !this.vfStripNonAlphanumeric &&
                          !this.vfTrimWhitespace && !this.vfCaseTransform && !replacements.length &&
                          !this.vfBold && !this.vfItalic && !this.vfStrike && !this.vfFontFamily && !this.vfFontSize
        if (isDefault) return null
        return {
          type: 'text',
          maxLength: this.vfMaxLength || null,
          stripHtml: this.vfStripHtml,
          stripNonAlphanumeric: this.vfStripNonAlphanumeric,
          trimWhitespace: this.vfTrimWhitespace,
          caseTransform: this.vfCaseTransform,
          replacements,
          bold: this.vfBold,
          italic: this.vfItalic,
          strike: this.vfStrike,
          fontFamily: this.vfFontFamily,
          fontSize: this.vfFontSize || null
        }
      }
      return null
    },
    onSave (event) {
      if (event && event.preventDefault) event.preventDefault()
      this.submitAttempted = true
      if (!String(this.formTitle || '').trim() || !String(this.formUniqueTitle || '').trim() || this.uniqueTitleBlocked) return

      const payload = {
        title: this.formTitle,
        uniqueTitle: this.formUniqueTitle,
        type: this.formType,
        relationshipType: this.formRelationshipType,
        relatedSpreadSheet: this.formRelatedSpreadSheet,
        unique: this.supportsUniqueConstraint ? this.formUnique : false,
        required: this.supportsColumnRequired ? this.formRequired : false,
        description: this.formDescription,
        defaultValue: this.supportsColumnDefault ? this.formDefaultValue : '',
        validationRules: { rules: this.formValidationRules },
        valueRules: this.supportsValueRules ? { rules: this.formValueRules } : { rules: [] },
        valueFormatting: this.buildValueFormattingOutput(),
        selectionMode: this.formType === 'multiselect' ? this.formSelectionMode : null,
        choices: this.formType === 'multiselect'
          ? this.formChoices.map(choice => String(choice || '').trim()).filter(Boolean)
          : null,
        autoResize: this.formType === 'image' ? this.formAutoResize : false,
        resizeWidth: this.formType === 'image' ? this.formResizeWidth : null,
        resizeHeight: this.formType === 'image' ? this.formResizeHeight : null,
        keepProportions: this.formType === 'image' ? this.formKeepProportions : false,
        dateOnly: this.formType === 'dateTime' ? !!this.formDateOnly : false,
        durationUnit: this.formType === 'duration' ? this.formDurationUnit : null,
        sequenceStart: this.formType === 'sequence' ? this.formSequenceStart : null,
        displayFormula: this.formType === 'relationship' ? (this.formRelationshipSettings.displayFormula || '') : '',
        cascadeFilters: this.formType === 'relationship' ? (this.formRelationshipSettings.cascadeFilters || []) : [],
        cascadeMatchMode: this.formType === 'relationship' ? (this.formRelationshipSettings.cascadeMatchMode || 'all') : 'all',
        showAllUntilParent: this.formType === 'relationship' ? !!this.formRelationshipSettings.showAllUntilParent : false,
        allowAdd: this.formType === 'relationship' ? !!this.formRelationshipSettings.allowAdd : false,
        addFields: this.formType === 'relationship' ? (this.formRelationshipSettings.addFields || []) : []
      }
      if (this.formType === 'formula') {
        payload.formula = this.formFormula
      }
      this.$emit('save', payload)
    },
    addChoice () {
      this.formChoices.push('')
    },
    removeChoice (index) {
      this.formChoices.splice(index, 1)
    },
    onHide () {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.value-formatting-section {
  border: 1px solid #3a3a37;
  border-radius: 6px;
  padding: 10px 12px;
  background: #141412;
  color: #e8e8e6;
}
.section-header {
  display: flex;
  align-items: center;
  color: #b0b0ae;
  font-size: 0.85rem;
}
.section-header .float-end {
  margin-left: auto;
  color: #7a7a78;
}
.value-formatting-section :deep(.form-label) {
  color: #8a8a88;
}
.value-formatting-section :deep(.text-muted) {
  color: #7a7a78 !important;
}
.choice-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}
.choice-remove {
  cursor: pointer;
  color: #f82471;
}
.add-choice {
  cursor: pointer;
  color: #6cb6ff;
  margin-top: 8px;
}
</style>
